import { SideBarDef } from 'ag-grid-community';
import { AgGridService } from '@approot/services/ag-grid/ag-grid.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { TradesCaptureService } from '@approot/services/trade-capture/trades-capture.service';
import { Header } from '@approot/models/ag-grid.model';
import { RequestStatus } from '@approot/models';

type GridData = {
  counterPartyA: string;
  counterPartyB: string;
  expectedFlow: number;
  expectedFlowCurrency: string;
  expectedFlowExpirationDateTime: string;
  expectedFlowSettlementDate: string;
  parentProductCode: string;
  'percentile_5%': number;
  'percentile_95%': number;
};
@Component({
  selector: 'app-load-scheduled-report',
  templateUrl: './load-scheduled-report.component.html',
  styleUrls: ['./load-scheduled-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadScheduledReportComponent implements OnInit {
  @Input() public identifier: string;
  @Input() public showModal: boolean;
  @Output() public modalOpenStatusChanged = new EventEmitter();
  public loadReportDateTimes: string[] = [];
  public rowData: GridData[];
  public loadReportDateTimesErrMessage: string;
  public headers: Header[] = [];
  public sideBarDef: SideBarDef;
  public fetchingReports: RequestStatus = '';
  constructor(
    private tradesCaptureService: TradesCaptureService,
    private agGridService: AgGridService,
    private changeRef: ChangeDetectorRef
  ) {
    this.sideBarDef = this.agGridService.sideBarDef;
  }

  public ngOnInit(): void {
    this.loadReportDateTimesErrMessage = 'Fetching available reports';
    this.tradesCaptureService
      .getUserInfoRequest({
        task: 'loadReportDateTimes',
        identifier: this.identifier
      })
      .subscribe({
        next: (reports: { reportCreationTime: string }[]) => {
          this.loadReportDateTimesErrMessage = '';
          if (reports?.length) {
            this.loadReportDateTimes = reports.map(
              element => element.reportCreationTime
            );
          } else {
            this.loadReportDateTimesErrMessage =
              'Failed to get available scheduled reports that can be loaded';
          }
          this.changeRef.markForCheck();
        },
        error: () => {
          this.loadReportDateTimesErrMessage =
            'Failed to get available scheduled reports that can be loaded';
          this.changeRef.markForCheck();
        }
      });
  }

  public selectReport(dateTime: string) {
    this.rowData = [];
    this.headers = [];
    this.loadReportDateTimesErrMessage = '';
    this.fetchingReports = 'LOADING';
    this.tradesCaptureService
      .getUserInfoRequest({
        task: 'loadReport',
        identifier: this.identifier,
        reportCreationTime: dateTime
      })
      .subscribe({
        next: (loadedReport: {
          date: string;
          isScheduledReport: string;
          ownerAndIdentifier: string;
          reportCreationTime: string;
          reportInput: any;
          reportOutput: {
            guiOutput: {
              grid: {
                data: any[];
                headers: string[];
                additionalHeaderDefinitions: any[];
              };
            };
          };
          status: string;
          statusCode: number;
          ttl: number;
        }) => {
          if (loadedReport?.reportOutput?.guiOutput?.grid) {
            const grid = loadedReport.reportOutput.guiOutput.grid;
            this.setGridData(grid);
            this.fetchingReports = 'DONE';
          } else {
            this.loadReportDateTimesErrMessage =
              'Failed to get the selected report';
            this.fetchingReports = 'ERROR';
          }
          this.changeRef.markForCheck();
        },
        error: () => {
          this.loadReportDateTimesErrMessage =
            'Failed to get the selected report';
          this.fetchingReports = 'ERROR';
          this.changeRef.markForCheck();
        }
      });
  }

  public modalChanged(event) {
    this.modalOpenStatusChanged.emit(event);
  }

  private setGridData(grid: {
    data: any[];
    headers: string[];
    additionalHeaderDefinitions: Header[];
  }) {
    this.loadReportDateTimes = null; // no  longer need to load reports
    if (grid.headers) {
      this.headers = this.agGridService.headers.filter(header =>
        grid.headers.includes(header.field)
      );
    }

    if (grid.additionalHeaderDefinitions) {
      // if there are additional headers sent
      this.headers.push(
        ...this.agGridService.formatHeaders(grid.additionalHeaderDefinitions) // format the headers and add to the headers array
      );
    }

    this.rowData = grid.data;
  }
}
